/**
 * Tiffany's welcome video (APS-259 step 4). Shown once on the dashboard for a
 * new person, and again from "Watch welcome video" on the game plan card.
 *
 * The clip is a circle in the middle of a white 9:16 frame, so it plays in a
 * square box cropped on the circle. No native controls and no autoplay: one
 * round button plays with sound, a tap pauses, "Replay" shows at the end.
 */
import React, { useEffect, useRef, useState } from 'react';
import { X, Play, Pause, RotateCcw } from 'react-feather';
import Modal from '../Modal';
import { Button } from '../ui';
import analytics from '../../services/analytics';
import { WELCOME_BUTTON, WELCOME_LINE, WELCOME_TITLE } from './gamePlanCopy';

export interface WelcomeCloseInfo {
  /** true when "Start my game plan" was pressed, false for X, Esc or backdrop. */
  started: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: (info: WelcomeCloseInfo) => void;
  videoUrl?: string;
  posterUrl?: string;
}

const DEFAULT_VIDEO = '/videos/welcome.mp4';
const DEFAULT_POSTER = '/videos/welcome-poster.jpg';

const WelcomeVideoModal: React.FC<Props> = ({ isOpen, onClose, videoUrl, posterUrl }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [started, setStarted] = useState(false);
  const watchedRef = useRef(0);
  const completedRef = useRef(false);
  const playedTrackedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    setPlaying(false);
    setEnded(false);
    setStarted(false);
    watchedRef.current = 0;
    completedRef.current = false;
    playedTrackedRef.current = false;
    try { analytics.track('welcome_video_shown', {}); } catch {}
  }, [isOpen]);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused || v.ended) {
      if (v.ended) v.currentTime = 0;
      v.play().catch(() => setPlaying(false));
    } else {
      v.pause();
    }
  };

  const replay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    setEnded(false);
    v.play().catch(() => setPlaying(false));
  };

  const close = (startedPlan: boolean) => {
    const v = videoRef.current;
    if (v && !v.paused) v.pause();
    try {
      analytics.track('welcome_video_closed', {
        watched_seconds: Math.round(watchedRef.current),
        completed: completedRef.current,
      });
    } catch {}
    onClose({ started: startedPlan });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => close(false)}
      labelledById="welcome-video-title"
      contentStyle={{ maxWidth: 420, borderRadius: 18, fontFamily: 'var(--aps-font-sans)' }}
    >
      <div style={{ position: 'relative', padding: '28px 24px 24px', textAlign: 'center' }}>
        <button
          type="button"
          onClick={() => close(false)}
          aria-label="Close"
          style={{
            position: 'absolute', top: 12, right: 12, width: 36, height: 36,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--aps-fill)', border: 'none', borderRadius: 999,
            cursor: 'pointer', color: 'var(--aps-text-muted)',
          }}
        >
          <X size={18} />
        </button>

        <h2
          id="welcome-video-title"
          style={{ margin: '4px 0 16px', fontSize: 22, fontWeight: 800, color: 'var(--aps-ink)' }}
        >
          {WELCOME_TITLE}
        </h2>

        <div
          style={{
            position: 'relative',
            width: 'min(320px, 80vw)',
            height: 'min(320px, 80vw)',
            margin: '0 auto',
            borderRadius: 16,
            overflow: 'hidden',
            background: 'var(--aps-surface)',
          }}
        >
          <video
            ref={videoRef}
            src={videoUrl || DEFAULT_VIDEO}
            poster={posterUrl || DEFAULT_POSTER}
            playsInline
            preload="metadata"
            onClick={toggle}
            onPlay={() => {
              setPlaying(true);
              setEnded(false);
              setStarted(true);
              if (!playedTrackedRef.current) {
                playedTrackedRef.current = true;
                try { analytics.track('welcome_video_played', {}); } catch {}
              }
            }}
            onPause={() => setPlaying(false)}
            onTimeUpdate={(e) => {
              const t = (e.target as HTMLVideoElement).currentTime;
              if (t > watchedRef.current) watchedRef.current = t;
            }}
            onEnded={() => {
              setPlaying(false);
              setEnded(true);
              if (!completedRef.current) {
                completedRef.current = true;
                try { analytics.track('welcome_video_completed', {}); } catch {}
              }
            }}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              // The circle's centre sits at 47% of the 9:16 frame. With cover in
              // a square box, 43.5% puts it in the middle of the box.
              objectPosition: '50% 43.5%',
              display: 'block',
              cursor: 'pointer',
            }}
          />
          {!playing && !ended && (
            <button
              type="button"
              onClick={toggle}
              aria-label={started ? 'Play' : "Play Tiffany's welcome video"}
              style={{
                position: 'absolute', left: '50%', bottom: '14%', transform: 'translateX(-50%)',
                width: 64, height: 64, borderRadius: 999, border: 'none',
                background: 'var(--aps-surface)', boxShadow: 'var(--aps-elev-3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: 'var(--aps-ink)',
              }}
            >
              <Play size={26} style={{ marginLeft: 3, fill: 'var(--aps-ink)' }} />
            </button>
          )}
          {ended && (
            <button
              type="button"
              onClick={replay}
              style={{
                position: 'absolute', left: '50%', bottom: '14%', transform: 'translateX(-50%)',
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px',
                borderRadius: 999, border: 'none', background: 'var(--aps-surface)',
                boxShadow: 'var(--aps-elev-3)', cursor: 'pointer',
                fontFamily: 'var(--aps-font-sans)', fontSize: 14, fontWeight: 700, color: 'var(--aps-ink)',
              }}
            >
              <RotateCcw size={15} />
              Replay
            </button>
          )}
          {playing && (
            <button
              type="button"
              onClick={toggle}
              aria-label="Pause"
              style={{
                position: 'absolute', right: 10, bottom: 10, width: 36, height: 36,
                borderRadius: 999, border: 'none', background: 'rgba(21, 17, 51, 0.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: 'var(--aps-on-ink)',
              }}
            >
              <Pause size={16} />
            </button>
          )}
        </div>

        <p style={{ margin: '16px 0 20px', fontSize: 15, lineHeight: 1.5, color: 'var(--aps-text-2)' }}>
          {WELCOME_LINE}
        </p>

        <Button variant="primary" size="lg" fullWidth onClick={() => close(true)}>
          {WELCOME_BUTTON}
        </Button>
      </div>
    </Modal>
  );
};

export default WelcomeVideoModal;
